import React, {
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';
import { Auth } from '@aws-amplify/auth';

import { logsApi } from 'api/bestpark/logs';
import { LOCAL_STORAGE_RESERVATION_REDIRECT } from 'constants/local-storage';
import {
  BOOKING_INFORMATION_PATH,
  CONTACT_DETAILS_PATH,
  REDIRECT_STRINGS,
} from 'constants/paths';
import { useAuth } from 'contexts/auth-context';
import { getItem, removeItem } from 'helpers/local-storage';
import { parseQuery } from 'helpers/path-helpers';
import { useTranslate } from 'hooks/translate';
import { getCurrentSession } from 'helpers/user-helpers';
import { useReservations } from 'contexts/reservations-context';

import './identification.scss';
import { useMutation, useQuery } from 'react-query';
import { reserveSlotsApi } from 'api/bestpark/reserve-slots';
import { parksApi } from 'api/bestpark/parks';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface VerificationFormValues {
  verificationCode: string;
}

interface VerificationProps {
  back: MouseEventHandler;
  existingCustomer: Customer | null;
  next: Function;
  sendCode: Function;
  user: any;
}

export default function Verification({
  back,
  existingCustomer,
  next,
  sendCode,
  user,
}: VerificationProps): ReactElement {
  const { setAuthState, setUsername } = useAuth();
  const location = useLocation();
  const { locale, t } = useTranslate();
  const [phoneNumber] = useState(getItem('BC.REGISTRATION_PHONE_NUMBER'));
  const [reCaptchaToken, setReCaptchaToken] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const getRecaptchaToken = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('resendAuthCode');
    setReCaptchaToken(token);
  }, [executeRecaptcha]);

  useEffect(() => {
    getRecaptchaToken();
  }, [getRecaptchaToken]);

  const verificationSchema = yup.object().shape<VerificationFormValues>({
    verificationCode: yup.string().required(t('login.verifyRequired')),
  });

  const { errors, handleSubmit, register } = useForm<VerificationFormValues>({
    validationSchema: verificationSchema,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      verificationCode: '',
    },
  });

  const { endDate, parkId, slot, startDate } = useReservations();

  const [isResending, setResending] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);

  const answerChallenge = useCallback(
    async (answer: string): Promise<any> => {
      try {
        await Auth.sendCustomChallengeAnswer(user, answer.toUpperCase());

        try {
          const queryStrings = parseQuery<{ redirect_to: string }>(
            location.search,
          );

          if (existingCustomer) {
            const session = await getCurrentSession();
            if (!session.accessToken) throw new Error('Incorrect code!');
            await setAuthState(session);
            logsApi.postLog('Customer', 'Login Successful', { phoneNumber });
          } else {
            setUsername(phoneNumber);
            const cognitoSession = await Auth.currentUserInfo();
            const session = await getCurrentSession(cognitoSession);
            if (!session.accessToken) throw new Error('Incorrect code!');
            await setAuthState(session);
          }

          if (getItem(LOCAL_STORAGE_RESERVATION_REDIRECT)) {
            removeItem(LOCAL_STORAGE_RESERVATION_REDIRECT);
            return { path: BOOKING_INFORMATION_PATH };
          } else if (
            queryStrings.redirect_to === REDIRECT_STRINGS.SERVICE_ENTRY
          ) {
            return { path: CONTACT_DETAILS_PATH };
          } else {
            return {
              path: `${BOOKING_INFORMATION_PATH}${
                existingCustomer ? location.search : ''
              }`,
            };
          }
        } catch (error) {
          logsApi.postLog('Customer', 'Authentication Code Incorrect', {
            phoneNumber,
          });
          return { authTry: true };
        }
      } catch (error) {
        logsApi.postLog('Customer', 'Authentication Code Invalidated', {
          phoneNumber,
        });
      }
    },
    [
      existingCustomer,
      location.search,
      phoneNumber,
      setAuthState,
      setUsername,
      user,
    ],
  );

  const { data: parkDetail } = useQuery(
    ['park', String(parkId), locale],
    parksApi.getPark,
    {
      // The query will not execute until parkId is greater than zero
      enabled: parkId > 0,
    },
  );

  const [postReserveParkingSlots] = useMutation(
    reserveSlotsApi.postReserveParkingSlots,
  );

  const onSubmit = useCallback(
    async ({ verificationCode }: VerificationFormValues): Promise<void> => {
      try {
        setLoading(true);
        const response = await answerChallenge(verificationCode);

        if (!response?.authTry && response?.path) {
          if (existingCustomer && !parkId) {
            window.location.href = '/profile';
          } else {
            await postReserveParkingSlots({
              data: {
                customerId: user.id,
                parkId: parkDetail?.id,
                parkingSlot: slot,
                startDate,
                endDate,
              },
            });
            next(response.path);
          }
        } else {
          setVerificationFailed(true);
        }
      } catch (error) {
        setVerificationFailed(true);
        logsApi.postLog('Customer', 'Verification Failed', {
          phoneNumber,
        });
      }

      setLoading(false);
    },
    [
      answerChallenge,
      existingCustomer,
      parkId,
      postReserveParkingSlots,
      user.id,
      parkDetail,
      slot,
      startDate,
      endDate,
      next,
      phoneNumber,
    ],
  );

  const resendCode = async (): Promise<void> => {
    setResending(true);
    await sendCode(phoneNumber, reCaptchaToken);
    setResending(false);
  };

  return (
    <div className='booking-content booking-verification'>
      <h1>{t('login.authContent')}</h1>
      <p className='booking-subtitle'>
        {phoneNumber}
        <span className='booking-content-action' onClick={back} />
      </p>
      <div className='booking-forms'>
        <label>
          {t('login.verify')} <span>{t('login.sms')}</span>
        </label>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input
            autoFocus
            className={`input-capitalized ${errors?.verificationCode?.message &&
              'input-error'}`}
            disabled={isResending || isLoading}
            id='verificationCode'
            name='verificationCode'
            ref={register()}
            type='text'
          />
          {(verificationFailed || errors?.verificationCode?.message) && (
            <div className='form-error'>
              {errors?.verificationCode?.message ??
                t('login.errors.invalidCode')}
            </div>
          )}
          <button disabled={isResending || isLoading} type='submit'>
            {isLoading ? t('loading') : t('reservation.registration.next')}
          </button>
        </form>
        <button
          className='transparent-button'
          disabled={isResending || isLoading}
          onClick={resendCode}
          type='button'
        >
          {isResending ? t('login.resending') : t('login.resendCode')}
        </button>
      </div>
    </div>
  );
}
